import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { getPlayerTitles, getStreakInfo } from '../lib/powerLevel';
import { useLanguage } from '../context/LanguageContext';
import { Shield, Zap, Target, Flame, Star, Lock, Repeat, Trophy } from 'lucide-react';

const iconMap = {
  shield: Shield, zap: Zap, target: Target, flame: Flame,
  star: Star, lock: Lock, repeat: Repeat,
};

export function TrophyCase({ stats }) {
  const { t } = useLanguage();
  const titles = getPlayerTitles(stats);
  const streak = getStreakInfo(stats?.current_streak || 0);

  return (
    <Card data-testid="trophy-case">
      <CardHeader>
        <CardTitle className="font-heading uppercase tracking-tight flex items-center gap-2">
          <Trophy className="h-5 w-5 text-accent" />
          {t('players.trophyCase')}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {/* Streak Badge */}
        {streak && (
          <div className={`flex items-center gap-2 p-3 rounded-lg bg-muted/50 mb-4 ${streak.color}`} data-testid="streak-badge">
            <div className="flex">
              {Array.from({ length: streak.flames }).map((_, i) => (
                <Flame key={`flame-${i}`} className="h-5 w-5 -ml-1 first:ml-0 animate-pulse" style={{ animationDelay: `${i * 0.15}s` }} />
              ))}
            </div>
            <span className="font-heading font-bold text-sm uppercase tracking-wider">{streak.label}</span>
            <span className="text-xs text-muted-foreground ml-auto">{stats?.current_streak} wins</span>
          </div>
        )}

        {/* Titles */}
        {titles.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-4">{t('players.noTrophies')}</p>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            {titles.map((title) => {
              const Icon = iconMap[title.icon] || Star;
              return (
                <div key={title.key} className="flex items-center gap-2 p-2.5 rounded-lg bg-muted/50 hover:bg-muted transition-colors" data-testid={`title-${title.key}`}>
                  <div className="p-1.5 rounded bg-accent/20 text-accent">
                    <Icon className="h-4 w-4" />
                  </div>
                  <div className="min-w-0">
                    <p className="font-heading font-bold text-xs uppercase tracking-wide truncate">{t(`titles.${title.key}`)}</p>
                    <p className="text-[10px] text-muted-foreground truncate">{title.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export function StreakFire({ currentStreak }) {
  const streak = getStreakInfo(currentStreak);
  if (!streak) return null;
  return (
    <span className={`inline-flex items-center gap-0.5 ${streak.color}`} data-testid="streak-fire">
      {Array.from({ length: streak.flames }).map((_, i) => (
        <Flame key={`fire-${i}`} className="h-3.5 w-3.5 animate-pulse" style={{ animationDelay: `${i * 0.15}s` }} />
      ))}
    </span>
  );
}
